// Copyright (c) 2026 Michael F. Kamprath
//
// app-config.js -- loader for public/app-config.json.
//
// Unlike themes.json / animations.json / voice-sets.json (each scoped
// to a specific data domain), app-config.json holds app-wide UI/UX
// tuning values that don't fit those domains.  Today that's the
// home-screen select-button animation duration; over time this will
// grow (splash transition timing, gear-icon long-press threshold, etc).
//
// Load is best-effort: if the fetch fails (offline first-run, bad JSON)
// we fall back to DEFAULTS rather than blocking the UI.  Callers use
// the accessor helpers (getHomeSelectAnimationMs, ...) which apply the
// same defaults, so a missing/partial config file degrades gracefully.

/**
 * Built-in defaults.  Kept in sync with public/app-config.json so
 * tests and runtime share a single source of truth.
 */
export const DEFAULTS = Object.freeze({
  home: {
    selectAnimationMs: 600,
    /**
     * Home-screen animated mascot sheep (src/ui/home-mascot.js).  All
     * numeric fields below are milliseconds unless noted; the string
     * fields are CSS length expressions applied as custom properties
     * on the mascot element.
     */
    mascot: Object.freeze({
      // --- Timings -------------------------------------------------------
      // Random gap between graze cycles.  The sheep stands head-up on
      // the static frame for a uniform random duration in
      // [grazeMinGapMs, grazeMaxGapMs] before triggering the next graze.
      grazeMinGapMs: 6000,
      grazeMaxGapMs: 10000,
      // How long the sheep spends in the head-down munching phase of
      // a graze cycle.  The runtime rounds this to the nearest multiple
      // of the 800 ms single-munch animation (eats_01..04), with a
      // minimum of one cycle.  Default 800 ms = one munch (the
      // original behaviour); set to 2400 for three cycles of visibly-
      // eating before the sheep raises its head again.  Independent
      // of the transitionToIdle (head lowering) and transitionFromIdle
      // (head rising) durations, which each remain 400 ms.
      grazeMunchDurationMs: 800,
      // Random gap between blink overlays (only fire while on the
      // static head-up frame -- suppressed during graze + reactions).
      blinkMinGapMs: 3000,
      blinkMaxGapMs: 5000,
      // How long the eyelid overlay stays visible for each blink.
      blinkDurationMs: 140,
      // --- Tap reaction mix ----------------------------------------------
      // Probability (0..1) of the silent rock reaction on a tap.  The
      // complementary probability fires the jump+baa reaction.  0.5 =
      // the 50/50 mix the spec calls for; 1 = always rock; 0 = always jump.
      rockProbability: 0.5,
      // --- Placement + size ----------------------------------------------
      // CSS length strings applied via custom properties on the mascot
      // element.  `width` sizes the outer wrapper (aspect ratio is
      // locked to 243/181 in the stylesheet).  `offsetX` and `offsetY`
      // translate the sheep from its centered default position between
      // the title and the nav buttons.  `jumpPeakY` controls how high
      // the sheep hops during the jump+baa reaction (percentage is of
      // the mascot's own height, so the motion stays proportional at
      // every size).  Null fields fall through to the stylesheet default.
      width: null,             // e.g., 'clamp(160px, 28vw, 280px)'
      offsetX: null,           // e.g., '-30px', '10%', '0'
      offsetY: null,           // e.g., '20px'
      jumpPeakY: null,         // e.g., '-28%'
    }),
  },
  /**
   * Quiz-mode tunings surfaced in app-config.json so they can be
   * adjusted without a rebuild.
   *
   * - plaqueEntrancePhase1Ms + plaqueEntrancePhase2Ms together control
   *   how long a single plaque takes to zoom in (default 200 + 100 =
   *   300 ms).  Increase for a more leisurely reveal.
   * - interPlaqueDelayMs is the stagger between consecutive plaques
   *   starting their entrance (default 50 ms -- legacy spec §3).
   *   Increase to let each plaque finish (or nearly finish) before
   *   the next one pops.
   * - postAnswerDelaySec is the silent beat between the success
   *   audio and round teardown (default 0.75 s, spec §2).
   *
   * Plaque SIZE is a per-theme concern -- edit themes.json's
   * `quizPlaque.imageSize` and `quizPlaque.displayScale` for the
   * theme you want to adjust.
   */
  quizTuning: Object.freeze({
    plaqueEntrancePhase1Ms: 200,
    plaqueEntrancePhase2Ms: 100,
    interPlaqueDelayMs: 50,
    postAnswerDelaySec: 0.75,
    // Scale overrides for the four per-theme quizPlaque values.  Each
    // is null by default so themes.json's per-theme values apply.
    // Setting any to a positive finite number makes THAT scale
    // uniform across all themes (useful for app-wide tuning without
    // editing 6 theme entries).
    //
    //   display   -- resting plaque scale after entrance settles
    //   touch     -- plaque scale while finger is held
    //   correct   -- plaque scale during the celebration rotation
    //   incorrect -- plaque scale after the dim animation
    //
    // Per-theme override lives in themes.json; the app-config value
    // (if non-null) wins.  Nullish coalescing means falsy-but-set
    // values like 0 are NOT treated as "missing" — 0 would be honoured
    // (though it would make the plaque invisible; use with care).
    scales: Object.freeze({
      display: null,
      touch: null,
      correct: null,
      incorrect: null,
    }),
    // Numeral font-size multiplier applied to quizPlaque numberFrame.h.
    // Legacy iOS used 0.80 (kFontScaleFactor -- 10% padding above and
    // below the visible glyph).  Precedence when the plaque renders:
    //   1. themes.json quizPlaque.fontSizeMultiplier (per-theme; wins
    //      when set to a positive number)
    //   2. app-config.json quizTuning.fontSizeMultiplier (global)
    //   3. baked-in 0.80 (legacy default)
    // null here means "no global override -- fall through to per-theme
    // or to 0.80".
    fontSizeMultiplier: null,
  }),
  quitButton: Object.freeze({
    standard: Object.freeze({
      imageUrl: '/images/ui/quit_game_button.png',
      size: 'clamp(2.25rem, 5vmin, 3.25rem)',
      center: Object.freeze({
        top: 'clamp(1.6rem, 3.5vmin, 2.4rem)',
        right: 'clamp(1.6rem, 3.5vmin, 2.4rem)',
      }),
    }),
    wide: Object.freeze({
      imageUrl: '/images/ui/quit_game_button.png',
      size: 'clamp(2.25rem, 5vmin, 3.25rem)',
      center: Object.freeze({
        top: 'clamp(1.6rem, 3.5vmin, 2.4rem)',
        right: 'clamp(1.6rem, 3.5vmin, 2.4rem)',
      }),
    }),
  }),
});

let cached = null;

/**
 * Fetch /app-config.json once and cache the result.  Returns the
 * parsed object, or `{}` if the fetch fails.  Never throws.
 *
 * @param {string} [url]  Override for tests.
 * @returns {Promise<object>}
 */
export async function loadAppConfig(url = '/app-config.json') {
  if (cached) return cached;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    cached = data && typeof data === 'object' ? data : {};
  } catch (err) {
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[app-config] load failed, using defaults:', err);
    }
    cached = {};
  }
  return cached;
}

// ---------------------------------------------------------------------------
// Accessors
// ---------------------------------------------------------------------------

/**
 * Duration (in ms) of the home-screen Learn/Quiz button enlarge/shrink
 * animation.  Falls back to 600ms if the config file doesn't set it or
 * provides a non-finite value.
 *
 * @param {object} [cfg]  Result of loadAppConfig().
 * @returns {number}
 */
export function getHomeSelectAnimationMs(cfg) {
  const v = cfg?.home?.selectAnimationMs;
  return Number.isFinite(v) && v >= 0 ? v : DEFAULTS.home.selectAnimationMs;
}

/**
 * Resolve mascot tunings from config, applying per-field defaults so a
 * partial `home.mascot` block still works (specifying just
 * `grazeMinGapMs` leaves everything else at its default).  Never throws.
 *
 * Numeric fields clamp via Number.isFinite + non-negative guard.
 * String fields (width, offsetX, offsetY, jumpPeakY) are kept as-is
 * when non-empty strings; any other value resolves to null so the
 * stylesheet's fallback applies.
 *
 * @param {object} [cfg]  Result of loadAppConfig().
 * @returns {{
 *   grazeMinGapMs: number,
 *   grazeMaxGapMs: number,
 *   grazeMunchDurationMs: number,
 *   blinkMinGapMs: number,
 *   blinkMaxGapMs: number,
 *   blinkDurationMs: number,
 *   rockProbability: number,
 *   width: string | null,
 *   offsetX: string | null,
 *   offsetY: string | null,
 *   jumpPeakY: string | null,
 * }}
 */
export function getHomeMascotConfig(cfg) {
  const d = DEFAULTS.home.mascot;
  const src = (cfg && typeof cfg.home === 'object' && cfg.home.mascot && typeof cfg.home.mascot === 'object')
    ? cfg.home.mascot : {};
  const num = (v, fb) => (Number.isFinite(v) && v >= 0 ? v : fb);
  const probability = (v, fb) => (Number.isFinite(v) && v >= 0 && v <= 1 ? v : fb);
  const str = (v) => (typeof v === 'string' && v.length > 0 ? v : null);
  return {
    grazeMinGapMs:        num(src.grazeMinGapMs,        d.grazeMinGapMs),
    grazeMaxGapMs:        num(src.grazeMaxGapMs,        d.grazeMaxGapMs),
    grazeMunchDurationMs: num(src.grazeMunchDurationMs, d.grazeMunchDurationMs),
    blinkMinGapMs:        num(src.blinkMinGapMs,        d.blinkMinGapMs),
    blinkMaxGapMs:        num(src.blinkMaxGapMs,        d.blinkMaxGapMs),
    blinkDurationMs:      num(src.blinkDurationMs,      d.blinkDurationMs),
    rockProbability:      probability(src.rockProbability, d.rockProbability),
    width:     str(src.width),
    offsetX:   str(src.offsetX),
    offsetY:   str(src.offsetY),
    jumpPeakY: str(src.jumpPeakY),
  };
}

/**
 * Resolve quiz-mode tunings from config, applying per-field defaults
 * so a partial `quizTuning` block in app-config.json still works (e.g.
 * specifying just `interPlaqueDelayMs` leaves the entrance phases at
 * their defaults).  Never throws.
 *
 * Scale overrides are returned as null when missing or invalid, so
 * the plaque manager can fall back to themes.json's per-theme value
 * via nullish-coalescing.  A positive finite number replaces the
 * per-theme value uniformly across every theme.
 *
 * @param {object} [cfg]  Result of loadAppConfig().
 * @returns {{
 *   plaqueEntrancePhase1Ms: number,
 *   plaqueEntrancePhase2Ms: number,
 *   interPlaqueDelayMs: number,
 *   postAnswerDelaySec: number,
 *   scales: {
 *     display: number | null,
 *     touch: number | null,
 *     correct: number | null,
 *     incorrect: number | null,
 *   },
 *   fontSizeMultiplier: number | null,
 * }}
 */
export function getQuizTuning(cfg) {
  const d = DEFAULTS.quizTuning;
  const src = (cfg && typeof cfg.quizTuning === 'object') ? cfg.quizTuning : {};
  const finite = (v, fb) => (Number.isFinite(v) && v >= 0 ? v : fb);
  const finitePositive = (v) => (Number.isFinite(v) && v > 0 ? v : null);
  const srcScales = (src.scales && typeof src.scales === 'object') ? src.scales : {};
  return {
    plaqueEntrancePhase1Ms: finite(src.plaqueEntrancePhase1Ms, d.plaqueEntrancePhase1Ms),
    plaqueEntrancePhase2Ms: finite(src.plaqueEntrancePhase2Ms, d.plaqueEntrancePhase2Ms),
    interPlaqueDelayMs:     finite(src.interPlaqueDelayMs,     d.interPlaqueDelayMs),
    postAnswerDelaySec:     finite(src.postAnswerDelaySec,     d.postAnswerDelaySec),
    scales: {
      display:   finitePositive(srcScales.display),
      touch:     finitePositive(srcScales.touch),
      correct:   finitePositive(srcScales.correct),
      incorrect: finitePositive(srcScales.incorrect),
    },
    fontSizeMultiplier: finitePositive(src.fontSizeMultiplier),
  };
}

/**
 * Quit-button styling, per layout variant.  The button is positioned
 * by its CENTER point relative to the top-right corner of the
 * viewport (so changing `size` doesn't move the centroid), and accepts
 * any CSS length string (clamp(), calc(), rem, vmin...) for the
 * dimensions and offsets.
 *
 * Each variant entry must supply:
 *   imageUrl       URL of the red-X PNG to render as a CSS background.
 *   size           CSS length used as both width and height.
 *   center.top     CSS length: distance from the top edge to the
 *                  button's vertical centre.
 *   center.right   CSS length: distance from the right edge to the
 *                  button's horizontal centre.
 *
 * Missing variants/fields fall back to DEFAULTS.quitButton.  Never
 * throws; a malformed config silently degrades to defaults.
 *
 * @param {object} [cfg]  Result of loadAppConfig().
 * @param {'standard' | 'wide'} [variant]  Defaults to 'standard'.
 * @returns {{ imageUrl: string, size: string, center: { top: string, right: string } }}
 */
export function getQuitButtonConfig(cfg, variant = 'standard') {
  const defaultsForVariant = DEFAULTS.quitButton[variant] || DEFAULTS.quitButton.standard;
  const fromCfg = cfg?.quitButton?.[variant];
  if (!fromCfg || typeof fromCfg !== 'object') {
    return {
      imageUrl: defaultsForVariant.imageUrl,
      size: defaultsForVariant.size,
      center: { ...defaultsForVariant.center },
    };
  }
  const center = (fromCfg.center && typeof fromCfg.center === 'object')
    ? fromCfg.center : null;
  return {
    imageUrl: typeof fromCfg.imageUrl === 'string' && fromCfg.imageUrl
      ? fromCfg.imageUrl : defaultsForVariant.imageUrl,
    size: typeof fromCfg.size === 'string' && fromCfg.size
      ? fromCfg.size : defaultsForVariant.size,
    center: {
      top: typeof center?.top === 'string' && center.top
        ? center.top : defaultsForVariant.center.top,
      right: typeof center?.right === 'string' && center.right
        ? center.right : defaultsForVariant.center.right,
    },
  };
}

// ---------------------------------------------------------------------------
// Test hooks
// ---------------------------------------------------------------------------

/** @internal drop the cache so the next loadAppConfig() re-fetches. */
export function __resetForTests() {
  cached = null;
}
